import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Bot, ChevronDown, MessageCircle } from "lucide-react";
import { useLanguage } from "@/i18n";
import { ASSETS, WHATSAPP_URL } from "@/data";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const PIXELS = Array.from({ length: 26 }, (_, i) => ({
  id: i,
  left: `${(i * 37) % 100}%`,
  top: `${(i * 53) % 100}%`,
  size: 3 + ((i * 7) % 5),
  duration: 5 + ((i * 3) % 7),
  delay: (i % 9) * 0.6,
  color: i % 3 === 0 ? "#8B5CF6" : "#00F0FF",
}));

export default function Hero() {
  const { t } = useLanguage();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="grid-bg noise-overlay relative flex min-h-screen items-center overflow-hidden pt-16"
    >
      <div className="scanline" />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-cyan-500/15 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-purple-600/20 blur-[120px]" />

      {PIXELS.map((p) => (
        <motion.span
          key={p.id}
          className="pointer-events-none absolute"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size, backgroundColor: p.color }}
          animate={{ y: [0, -22, 0], opacity: [0.15, 0.7, 0.15] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 px-4 pb-24 pt-16 sm:px-6 lg:grid-cols-12 lg:items-center lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-badge"
            className="font-pixel mb-8 inline-flex items-center gap-3 border border-cyan-400/30 bg-[#0A0D24]/70 px-4 py-2 text-[10px] tracking-[0.3em] text-cyan-300"
          >
            <span className="blink h-2 w-2 bg-cyan-400" />
            {t.hero.badge}
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            <span className="font-pixel mb-3 block text-xs tracking-[0.4em] text-purple-400 sm:text-sm">
              <MaskedLine delay={0.25}>NAVX —</MaskedLine>
            </span>
            <MaskedLine delay={0.4}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.55} className="stroke-text">
              {t.hero.line2}
            </MaskedLine>
            <MaskedLine delay={0.7} className="text-glow-cyan text-cyan-300">
              {t.hero.line3}
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="pixel-corners group flex items-center gap-3 bg-cyan-400 px-7 py-4 text-sm font-bold uppercase tracking-widest text-[#050714] transition-all duration-300 hover:bg-white hover:shadow-[0_0_40px_rgba(0,240,255,0.55)]"
            >
              <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
              {t.hero.ctaWhatsapp}
            </a>
            <a
              href="#bots"
              data-testid="hero-cta-bots"
              className="pixel-corners group flex items-center gap-3 border border-purple-500/50 bg-purple-500/10 px-7 py-4 text-sm font-bold uppercase tracking-widest text-purple-300 transition-all duration-300 hover:border-purple-400 hover:bg-purple-500/25 hover:shadow-[0_0_32px_rgba(139,92,246,0.4)]"
            >
              <Bot className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
              {t.hero.ctaBots}
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 flex divide-x divide-white/10 border-y border-white/10"
          >
            {t.hero.stats.map((s) => (
              <div key={s.label} className="flex-1 px-4 py-4 first:pl-0 sm:px-6">
                <div className="font-pixel text-xl text-cyan-300 sm:text-2xl">{s.value}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block" style={{ perspective: 1000 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative mx-auto w-80 xl:w-96"
          >
            <div className="absolute -inset-8 border border-dashed border-cyan-400/20" style={{ transform: "translateZ(-40px)" }} />
            <div className="pixel-corners float-slow overflow-hidden border-2 border-cyan-400/50 bg-[#0A0D24] shadow-[0_0_60px_rgba(0,240,255,0.3)]">
              <img
                src={ASSETS.logo}
                alt="NavX — compasso pixel art"
                data-testid="hero-logo"
                className="h-full w-full object-cover"
              />
            </div>
            <div
              className="font-pixel absolute -bottom-6 -right-6 border border-purple-500/50 bg-[#0A0D24] px-3 py-2 text-[10px] tracking-widest text-purple-300"
              style={{ transform: "translateZ(40px)" }}
            >
              EST. 2026
            </div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#bots"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-500 md:flex"
      >
        <span className="font-pixel text-[9px] uppercase tracking-[0.35em]">{t.hero.scroll}</span>
        <ChevronDown className="h-4 w-4 animate-bounce text-cyan-400" />
      </motion.a>
    </section>
  );
}
